// ============================================================
// src/hooks/useNotificationPrefs.js
//
// ── WHAT THIS FILE DOES ──────────────────────────────────────
// Manages the two pieces of data the notification system needs
// from the user in order to send smart, timed reminders:
//
//   1. PREP TIME — how long it takes the user to get ready.
//      Stored as prepMinutes (integer, e.g. 150 = 2h 30m).
//      The "Get Ready" notification fires at:
//        gig_time - prepMinutes
//
//   2. HOME LOCATION — where the user travels FROM.
//      The user types an address; we geocode it with Nominatim
//      and store the lat/lng so the Edge Function can call OSRM
//      at send time without re-geocoding every time.
//      The "Leave Now" notification fires at:
//        gig_time - osrmTravelMinutes - LEAVE_BUFFER_MINUTES
//
// ── REQUIRED SUPABASE SQL ────────────────────────────────────
// Run once in your Supabase SQL editor:
//
//   create table notification_preferences (
//     user_id      uuid    primary key references auth.users,
//     prep_minutes int     not null default 120,
//     home_address text,
//     home_lat     float,
//     home_lng     float,
//     updated_at   timestamptz default now()
//   );
//
//   alter table notification_preferences enable row level security;
//
//   create policy "Users manage own prefs"
//     on notification_preferences for all
//     using  (auth.uid() = user_id)
//     with check (auth.uid() = user_id);
//
// ── NOMINATIM USAGE POLICY ───────────────────────────────────
// We call Nominatim only when the user saves a new address.
// This is a one-off user-triggered action, not a background
// poll, so it complies with the Nominatim usage policy:
//   https://operations.osmfoundation.org/policies/nominatim/
// We include a descriptive User-Agent as required.
// ============================================================

import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'


// ── CONSTANTS ─────────────────────────────────────────────────

// How many minutes of buffer we add on top of the OSRM travel time
// for the "leave now" notification. 15 min is enough to account for
// getting out the door, parking, and finding the venue entrance.
export const LEAVE_BUFFER_MINUTES = 15

// Nominatim endpoint — no auth required, but we must set User-Agent
const NOMINATIM_URL        = 'https://nominatim.openstreetmap.org/search'
const NOMINATIM_USER_AGENT = 'DragPocketPlanner/1.0 (https://drag-helper.netlify.app)'

// Edge Function URL — used to trigger re-scheduling after prefs save
const EDGE_FN_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-gig-reminders`


// ============================================================
// geocodeAddress
// Standalone utility — exported so other files can import it
// if needed (e.g. the gig form for future venue geocoding).
//
// Takes a plain text address string and returns
//   { lat: number, lng: number, displayName: string }
// or throws an Error if nothing was found.
// ============================================================
export async function geocodeAddress(address) {
  // Build query params — q is the freetext address
  const params = new URLSearchParams({
    q:              address,
    format:         'json',
    limit:          '1',
    // limit=1 = we only want the best match, not a list
    addressdetails: '0',
    // addressdetails=0 = skip the verbose breakdown; we just need lat/lng
  })

  const response = await fetch(`${NOMINATIM_URL}?${params}`, {
    headers: {
      'User-Agent':    NOMINATIM_USER_AGENT,
      'Accept-Language': 'en',
      // Accept-Language nudges Nominatim to return English place names
    },
  })

  if (!response.ok) {
    throw new Error(`Nominatim returned ${response.status}. Try again shortly.`)
  }

  const results = await response.json()

  if (!results || results.length === 0) {
    throw new Error('Address not found. Try adding a city or zip code.')
  }

  const best = results[0]
  return {
    lat:         parseFloat(best.lat),
    lng:         parseFloat(best.lon),
    displayName: best.display_name,
    // display_name = the full formatted address Nominatim matched to
    // useful for showing the user what we actually geocoded
  }
}


// ============================================================
// useNotificationPrefs HOOK
// ============================================================

export function useNotificationPrefs() {

  // ── STATE ─────────────────────────────────────────────────

  const [prepMinutes,          setPrepMinutes]          = useState(120)
  // default 120 = 2 hours -- a reasonable prep time for a drag show
  // stored as total minutes; UI converts to/from hours+minutes

  const [advanceNoticeMinutes, setAdvanceNoticeMinutes] = useState(30)
  // how many minutes before each exact notification time the warning fires
  // e.g. 30 = "Get ready in 30 minutes" fires 30 min before the get-ready time
  // default 30 minutes -- user can change it in Settings

  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
  )
  // timezone = IANA timezone string detected from the browser at mount time
  // e.g. "America/New_York", "America/Chicago", "America/Los_Angeles"
  // Sent to Supabase so the Edge Function can correctly interpret gig times.
  // Intl.DateTimeFormat().resolvedOptions().timeZone is the most reliable
  // cross-platform way to detect the user's local timezone in the browser.

  const [homeAddress,   setHomeAddress]   = useState('')
  // the raw text the user typed — shown in the input field

  const [homeLat,       setHomeLat]       = useState(null)
  // latitude of the geocoded home address (null = not geocoded yet)

  const [homeLng,       setHomeLng]       = useState(null)
  // longitude of the geocoded home address

  const [geocodedLabel, setGeocodedLabel] = useState('')
  // the display_name Nominatim returned — shown as a confirmation
  // so the user knows we geocoded the right place

  const [loading,       setLoading]       = useState(true)
  // true while fetching saved prefs from Supabase on mount

  const [saving,        setSaving]        = useState(false)
  // true while the save call is in-flight

  const [geocoding,     setGeocoding]     = useState(false)
  // true while the Nominatim request is running

  const [error,         setError]         = useState(null)
  // null = clean, string = show this message

  const [saveSuccess,   setSaveSuccess]   = useState(false)
  // true for 3 seconds after a successful save


  // ── LOAD SAVED PREFS ON MOUNT ─────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        const { data, error: fetchError } = await supabase
          .from('notification_preferences')
          .select('prep_minutes, advance_notice_minutes, home_address, home_lat, home_lng, timezone')
          .eq('user_id', user.id)
          .single()
          // .single() returns the row object or null
          // PGRST116 = no row found; any other error = real problem

        if (fetchError && fetchError.code !== 'PGRST116') {
          throw fetchError
        }

        if (data) {
          // User has saved prefs -- populate all state from the row
          setPrepMinutes(data.prep_minutes ?? 120)
          setAdvanceNoticeMinutes(data.advance_notice_minutes ?? 30)
          setHomeAddress(data.home_address ?? '')
          setHomeLat(data.home_lat ?? null)
          setHomeLng(data.home_lng ?? null)
          if (data.home_address) {
            setGeocodedLabel(data.home_address)
          }
          // Always re-detect timezone from the browser on load --
          // the stored value may be stale if the user has travelled.
          // We set state here and the next save() will update Supabase.
          const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone
          if (detectedTz) setTimezone(detectedTz)
        }
        // if no row: leave defaults — user hasn't set prefs yet

      } catch (err) {
        console.error('useNotificationPrefs: load error:', err)
        setError('Could not load notification settings.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])
  // [] = run once on mount


  // ── geocodeAndPreview ─────────────────────────────────────
  // Geocodes the current homeAddress input and updates the
  // homeLat/homeLng state. Does NOT save to Supabase yet.
  // The user still needs to press "Save" to persist.
  const geocodeAndPreview = useCallback(async () => {
    if (!homeAddress.trim()) {
      setError('Please enter an address first.')
      return
    }

    setGeocoding(true)
    setError(null)
    setGeocodedLabel('')

    try {
      const result = await geocodeAddress(homeAddress)

      setHomeLat(result.lat)
      setHomeLng(result.lng)
      setGeocodedLabel(result.displayName)
      // show the full matched address so the user can verify it's correct

    } catch (err) {
      setError(err.message)
      setHomeLat(null)
      setHomeLng(null)
    } finally {
      setGeocoding(false)
    }
  }, [homeAddress])


  // ── useCurrentLocation ────────────────────────────────────
  // Alternative to typing an address: get the browser GPS position
  // and reverse-geocode it with Nominatim to get a readable label.
  const useCurrentLocation = useCallback(async () => {
    if (!('geolocation' in navigator)) {
      setError('Geolocation is not available in this browser.')
      return
    }

    setGeocoding(true)
    setError(null)

    try {
      // navigator.geolocation.getCurrentPosition is callback-based
      // wrap it in a Promise so we can await it cleanly
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          // enableHighAccuracy: false = faster, uses cell/wifi triangulation
          // true would require GPS and take much longer
          timeout: 10000,
          // give up after 10 seconds if no position comes back
        })
      })

      const lat = position.coords.latitude
      const lng = position.coords.longitude

      setHomeLat(lat)
      setHomeLng(lng)

      // Reverse geocode to get a human-readable address for the input field
      const reverseParams = new URLSearchParams({
        lat:    String(lat),
        lon:    String(lng),
        format: 'json',
      })

      const reverseRes = await fetch(
        `https://nominatim.openstreetmap.org/reverse?${reverseParams}`,
        { headers: { 'User-Agent': NOMINATIM_USER_AGENT } }
      )

      if (reverseRes.ok) {
        const reverseData = await reverseRes.json()
        const label = reverseData.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
        setHomeAddress(label)
        setGeocodedLabel(label)
      } else {
        // reverse geocoding failed but we still have the coordinates
        const fallback = `${lat.toFixed(4)}, ${lng.toFixed(4)}`
        setHomeAddress(fallback)
        setGeocodedLabel(fallback)
      }

    } catch (err) {
      // GeolocationPositionError codes:
      //   1 = PERMISSION_DENIED
      //   2 = POSITION_UNAVAILABLE
      //   3 = TIMEOUT
      if (err.code === 1) {
        setError('Location permission denied. Type your address manually instead.')
      } else {
        setError('Could not get your location. Type your address manually.')
      }
    } finally {
      setGeocoding(false)
    }
  }, [])


  // ── savePrefs ─────────────────────────────────────────────
  // Upserts the notification_preferences row for the current user.
  // Called when the user clicks "Save" in the settings section.
  //
  // FIX (Bug 4): After saving prefs, we immediately re-schedule all
  // future gigs via the Edge Function. This handles the common case
  // where a user adds gigs BEFORE setting up their home address —
  // those gigs silently got a { skipped: 'no prefs' } response and
  // never had scheduled_notifications rows written for them.
  // Re-scheduling here fills that gap without requiring the user to
  // re-save every gig manually.
  const savePrefs = useCallback(async () => {
    setSaving(true)
    setError(null)
    setSaveSuccess(false)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('Not authenticated')

      if (!homeLat || !homeLng) {
        throw new Error('Please geocode your home address before saving. Click "Find Address" or "Use My Location".')
      }

      // Re-detect timezone at save time in case it changed
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || timezone

      const { error: upsertError } = await supabase
        .from('notification_preferences')
        .upsert(
          {
            user_id:                user.id,
            prep_minutes:           prepMinutes,
            advance_notice_minutes: advanceNoticeMinutes,
            home_address:           homeAddress,
            home_lat:               homeLat,
            home_lng:               homeLng,
            timezone:               currentTimezone,
            updated_at:             new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        )

      if (upsertError) throw upsertError

      // ── Re-schedule all future gigs ──────────────────────
      // Now that we have valid home coords, kick off the Edge Function
      // in 'schedule' mode for every future gig. This is fire-and-forget
      // — we don't await each call individually so the UI stays fast.
      //
      // Why this matters:
      //   If the user created gigs before setting their home address,
      //   those gigs never got rows in scheduled_notifications because
      //   the Edge Function returned { skipped: 'no prefs' }.
      //   This loop fills that gap automatically.
      try {
        const todayStr = new Intl.DateTimeFormat('en-CA').format(new Date())
        // en-CA locale gives YYYY-MM-DD format natively — same format as gig.date

        const { data: futureGigs } = await supabase
          .from('gigs')
          .select('id')
          .eq('user_id', user.id)
          .gte('date', todayStr)
          // gte = greater than or equal to — includes today's gigs too

        const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

        for (const gig of (futureGigs ?? [])) {
          // Fire each schedule call without awaiting — we don't want one
          // slow Mapbox call to block the whole loop or the UI save response
          fetch(EDGE_FN_URL, {
            method: 'POST',
            headers: {
              'Content-Type':  'application/json',
              'Authorization': `Bearer ${anonKey}`,
            },
            body: JSON.stringify({ mode: 'schedule', gigId: gig.id, userId: user.id }),
          }).catch(err => console.error(`[Push] Re-schedule failed for gig ${gig.id}:`, err))
        }

        if (futureGigs?.length) {
          console.log(`[Push] Re-scheduling ${futureGigs.length} future gig(s) after prefs save`)
        }
      } catch (rescheduleErr) {
        // Non-fatal — the prefs save itself succeeded.
        // The morning-of recalc (Layer 2) will catch any missed gigs.
        console.warn('[Push] Re-schedule loop failed (non-fatal):', rescheduleErr)
      }

      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
      // hide the success message after 3 seconds

    } catch (err) {
      setError(err.message || 'Failed to save notification settings.')
      console.error('useNotificationPrefs: save error:', err)
    } finally {
      setSaving(false)
    }
  }, [prepMinutes, advanceNoticeMinutes, homeAddress, homeLat, homeLng, timezone])


  // ── RETURN ────────────────────────────────────────────────
  // FIX Bug 2: added setHomeLat, setHomeLng, setGeocodedLabel
  // so settings.js can call them from handleMapLocationChange.
  // Without these exports the MapPicker pin drag had no effect
  // on the parent state.
  return {
    // State
    prepMinutes,                // number — total minutes of prep time
    setPrepMinutes,             // (n: number) => void
    advanceNoticeMinutes,       // number — minutes of advance warning before each notification
    setAdvanceNoticeMinutes,    // (n: number) => void
    homeAddress,                // string — text in the address input
    setHomeAddress,             // (s: string) => void
    homeLat,                    // number | null — geocoded latitude
    setHomeLat,                 // (n: number | null) => void
    homeLng,                    // number | null — geocoded longitude
    setHomeLng,                 // (n: number | null) => void
    geocodedLabel,              // string — Nominatim display_name for confirmation
    setGeocodedLabel,           // (s: string) => void
    // Status flags
    loading,                    // boolean — initial load from Supabase
    saving,                     // boolean — save in progress
    geocoding,                  // boolean — Nominatim request in progress
    error,                      // string | null
    saveSuccess,                // boolean — show success banner for 3s
    timezone,                   // string -- IANA timezone, e.g. 'America/New_York'
    // Actions
    geocodeAndPreview,          // () => Promise<void> — geocode the typed address
    useCurrentLocation,         // () => Promise<void> — use browser GPS
    savePrefs,                  // () => Promise<void> — upsert to Supabase
  }
}